import React, {
    FC,
    FormEvent,
    LegacyRef,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { Chip } from '@material-ui/core'
import useIsMountedRef from 'use-is-mounted-ref'
import { BehaviorSubject, Subscription } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { SelectOption, SelectOptions } from '../Types'
import { ReactState } from '../../../Types/Types'
import { RangeEntryWrapper } from './Types'
import TextInput from '../../TextInput'
import IconButton, { IconButtonType } from '../../IconButton'
import Icon, { IconName } from '../../Icon'
import { DASH, EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import PopperContent from '../../PopperContent'
import { SelectRangeItemState } from '../Constants'
import {
    IsStringEmpty,
    isStringNotEmpty,
    IsVarEmpty,
    isVarNotEmpty
} from '../../../../../NonReact/utils/GlobalFunctions'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { RangeComboLanguage } from '../../../../../../i18n'
import { LanguageContext } from '../../../Contexts'
import {
    addRangeEntryWrapper,
    removeRangeEntryWrapper
} from './Functions/AddRemoveRangeEntryWrapper/AddRemoveRangeEntryWrapper'
import Label from '../../Label'
import { ReactStateRef, useStateAsRef } from '../../../Hooks/useStateAsRef'
import { rangeWrappersToString, rangeWrapperToString } from './Functions/StringMappers/RangeWrappersToStringMapper'
import { stringToRangeWrappers } from './Functions/StringMappers/StringToRangeWrappers'
import { StringUtils } from '../../../../../NonReact/utils/StringUtils'

type Props = {
    label?: string
    className?: string
    options: SelectOptions<string>
    allItemIndex?: number
    originalValue: Nullable<string>
    onChange?: (value?: string, ranges?: Nullable<RangeEntryWrapper[]>) => void
    asyncSearchingFunction?: (searchQuery: string) => Promise<SelectOptions<string>>
}

export const RangeCombo: FC<Props> = ({
    label,
    className,
    options,
    allItemIndex,
    originalValue,
    onChange,
    asyncSearchingFunction
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RangeComboLanguage.NAME)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const rcInputAreaRef: Definable<LegacyRef<HTMLDivElement>> = useRef<HTMLDivElement>(null)
    const spanInputRef: Definable<LegacyRef<HTMLSpanElement>> = useRef<HTMLSpanElement>(null)

    const [isPopperOpen, setIsPopperOpen]: ReactState<boolean> = useState<boolean>(false)
    const [isTextEntryMode, setIsTextEntryMode]: ReactState<boolean> = useState<boolean>(false)
    const [enteredValue, setEnteredValue]: ReactState<string> = useState<string>(originalValue ?? EMPTY_STRING)
    const [selectedRanges, setSelectedRanges]: ReactState<Nullable<RangeEntryWrapper[]>> =
        useState<Nullable<RangeEntryWrapper[]>>()
    const [currentStringValue, setCurrentStringValue]: ReactStateRef<Nullable<string>> =
        useStateAsRef<Nullable<string>>(undefined)
    const [currentlySelectedIndex, setCurrentlySelectedIndex]: ReactState<Nullable<number>> =
        useState<Nullable<number>>()
    const [search, setSearch]: ReactState<Nullable<string>> = useState<Nullable<string>>(null)
    const [isAdding, setIsAdding]: ReactState<boolean> = useState<boolean>(false)
    const [addingValue, setAddingValue]: ReactState<string> = useState<string>(EMPTY_STRING)
    const [searchedOptions, setSearchedOptions]: ReactState<SelectOptions<string>> = useState<SelectOptions<string>>([])

    const calculateOptionState = useCallback(
        (optionsToCalculate: SelectOptions<string>): SelectOptions<string> =>
            optionsToCalculate.map((option: SelectOption<string>): SelectOption<string> => {
                const selectedRange: Nullable<RangeEntryWrapper> = selectedRanges?.find(
                    (rangeWrapper: Nullable<RangeEntryWrapper>): boolean => {
                        if (isVarNotEmpty(rangeWrapper)) {
                            const optionValueTemplate: string = option.value.toString().replace(/\d/g, EMPTY_STRING)

                            if (!rangeWrapper.isRange) {
                                return (
                                    StringUtils.convertToNumberIfPossible(option.value)?.toString() ===
                                        rangeWrapper.value.toString() &&
                                    optionValueTemplate === rangeWrapper.displayValue.replace(/\d/g, EMPTY_STRING)
                                )
                            }

                            const optionValueAsNumber: number = Number(option.value.replace(/[A-z]/g, EMPTY_STRING))

                            return (
                                optionValueAsNumber >= rangeWrapper.value.min &&
                                optionValueAsNumber <= rangeWrapper.value.max &&
                                optionValueTemplate === rangeWrapper.value.minDisplayValue.replace(/\d/g, EMPTY_STRING)
                            )
                        }

                        return false
                    }
                )

                if (isVarNotEmpty(selectedRange)) {
                    option.itemState = selectedRange.included
                        ? SelectRangeItemState.INCLUDED
                        : SelectRangeItemState.EXCLUDED
                } else {
                    option.itemState = SelectRangeItemState.NONE
                }

                return option
            }),
        [selectedRanges]
    )

    const calculatedOptions: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => calculateOptionState(options),
        [options, selectedRanges]
    )

    const switchMode = useCallback(
        (): void =>
            setIsTextEntryMode((currentValue: boolean): boolean => {
                if (currentValue && isMounted.current) {
                    setSelectedRanges(stringToRangeWrappers(enteredValue))
                } else if (isMounted.current) {
                    setEnteredValue(rangeWrappersToString(selectedRanges))
                }

                return !currentValue
            }),
        [enteredValue, selectedRanges]
    )

    const toCheckBox = (itemState: SelectRangeItemState): ReactElement => {
        switch (itemState) {
            case SelectRangeItemState.INCLUDED:
                return (
                    <div className='state-icon item-included'>
                        <Icon iconName={IconName.CHECKBOX_CHECKED} />
                    </div>
                )
            case SelectRangeItemState.EXCLUDED:
                return (
                    <div className='state-icon item-excluded'>
                        <Icon iconName={IconName.CHECKBOX_MINUS} />
                    </div>
                )
            case SelectRangeItemState.NONE:
            default:
                return (
                    <div className='state-icon item-none'>
                        <Icon iconName={IconName.CHECKBOX_BLANK} />
                    </div>
                )
        }
    }

    const openPopper = (): void => {
        if (isMounted.current) {
            setIsPopperOpen(true)
        }
    }

    const addNewOptions = (event: React.KeyboardEvent<HTMLSpanElement> | React.FocusEvent<HTMLSpanElement>): void => {
        if (isMounted.current) {
            setSelectedRanges((currentRanges: Nullable<RangeEntryWrapper[]>): Nullable<RangeEntryWrapper[]> => {
                if (isStringNotEmpty(addingValue)) {
                    return addRangeEntryWrapper(addingValue, true, currentRanges)
                }

                return currentRanges
            })
            setIsAdding(false)
            setAddingValue(EMPTY_STRING)

            event.currentTarget.textContent = EMPTY_STRING
            event?.currentTarget?.blur()
        }
    }

    const doLocalSearch = useCallback(
        (searchQuery: string, isRange?: boolean): void => {
            if (IsStringEmpty(searchQuery)) {
                setSearchedOptions(calculatedOptions)
            } else if (isRange) {
                const rangeSearch: string[] = searchQuery.split(DASH)
                const isValidRangeSearch: boolean =
                    searchQuery.includes(DASH) &&
                    rangeSearch.length === 2 &&
                    !isNaN(parseInt(rangeSearch[0], 10)) &&
                    !isNaN(parseInt(rangeSearch[1], 10))

                setSearchedOptions(
                    calculatedOptions.filter((option: SelectOption<string>): boolean => {
                        if (isValidRangeSearch) {
                            const optionValueAsNumber: number = parseInt(option.value, 10)

                            if (!isNaN(optionValueAsNumber)) {
                                return (
                                    optionValueAsNumber >= parseInt(rangeSearch[0], 10) &&
                                    optionValueAsNumber <= parseInt(rangeSearch[1], 10)
                                )
                            }
                        }

                        return (
                            option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            option.value.toLowerCase().includes(searchQuery)
                        )
                    })
                )
            } else {
                setSearchedOptions(
                    calculatedOptions.filter(
                        (option: SelectOption<string>): boolean =>
                            option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            option.value.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                )
            }
        },
        [calculatedOptions]
    )

    const [searchSubject, setSearchSubject]: ReactState<BehaviorSubject<string>> = useState<BehaviorSubject<string>>(
        new BehaviorSubject<string>(EMPTY_STRING)
    )

    useEffect(() => {
        const newSearchSubject: BehaviorSubject<string> = new BehaviorSubject<string>(EMPTY_STRING)

        setSearchSubject((currentSubject: BehaviorSubject<string>): BehaviorSubject<string> => {
            currentSubject.complete()
            return newSearchSubject
        })

        const subscription: Subscription = newSearchSubject
            .pipe(debounceTime(300))
            .subscribe((currentSearch: string): void => {
                if (IsStringEmpty(currentSearch)) {
                    setSearchedOptions(calculatedOptions)
                    // eslint-disable-next-line
                } else if (/^\d*x?\d*-\d*x?\d*$/.test(currentSearch)) {
                    doLocalSearch(currentSearch, true)
                } else if (isVarNotEmpty(asyncSearchingFunction) && currentSearch.length > 1) {
                    asyncSearchingFunction(currentSearch).then((results: SelectOptions<string>): void => {
                        if (isMounted.current) {
                            setSearchedOptions(calculateOptionState(results))
                        }
                    })
                } else {
                    doLocalSearch(currentSearch)
                }
            })

        return (): void => {
            subscription.unsubscribe()
            searchSubject.complete()
        }
    }, [calculatedOptions, asyncSearchingFunction])

    useEffect((): void => {
        if (isMounted.current) {
            if (!IsVarEmpty(search)) {
                searchSubject.next(search)
            }
        }
    }, [search, searchSubject, asyncSearchingFunction, calculatedOptions])

    useEffect((): void => {
        if (isVarNotEmpty(allItemIndex) && options.length > allItemIndex && selectedRanges) {
            if (
                selectedRanges.length === 0 ||
                (selectedRanges.length === 1 && selectedRanges[0].value === options[allItemIndex].value)
            ) {
                setSelectedRanges([
                    {
                        included: true,
                        isRange: false,
                        displayValue: options[allItemIndex].value,
                        value: options[allItemIndex].value
                    }
                ])
            } else {
                setSelectedRanges(
                    (currentRanges: Nullable<RangeEntryWrapper[]>): Nullable<RangeEntryWrapper[]> =>
                        currentRanges?.filter(
                            (range: RangeEntryWrapper): boolean =>
                                range.isRange || range.value !== options[allItemIndex].value
                        )
                )
            }
        }
    }, [allItemIndex, options, JSON.stringify(selectedRanges)])

    useEffect((): void => {
        if (
            originalValue &&
            isMounted.current &&
            (IsStringEmpty(currentStringValue.current) || originalValue !== currentStringValue.current)
        ) {
            const allItemValue: string = allItemIndex ? options?.[allItemIndex]?.value : EMPTY_STRING

            setSelectedRanges(
                originalValue
                    ? stringToRangeWrappers(originalValue)
                    : [
                          {
                              included: true,
                              isRange: false,
                              displayValue: allItemValue,
                              value: allItemValue
                          }
                      ]
            )
        }
    }, [originalValue])

    useEffect((): void => {
        if (isMounted.current) {
            const newStringValue: string = rangeWrappersToString(selectedRanges, true)

            setCurrentStringValue(newStringValue)
            onChange?.(newStringValue, selectedRanges)
        }
    }, [JSON.stringify(selectedRanges)])

    return (
        <div className={`range-combo ${isStringNotEmpty(className) ? className : ''}`}>
            {isStringNotEmpty(label) ? <Label>{label}</Label> : <></>}
            {isTextEntryMode ? (
                <TextInput
                    value={enteredValue}
                    onBlur={switchMode}
                    onChange={(newValue: string): void => {
                        if (isMounted.current) {
                            setEnteredValue(newValue)
                        }
                    }}
                    adornmentEnd={
                        <div className='rc-adornment' onClick={switchMode}>
                            <Icon iconName={IconName.PENCIL} />
                        </div>
                    }
                />
            ) : (
                <div className='rc-input-area input MuiInputBase-root MuiInput-underline' ref={rcInputAreaRef}>
                    <div className='rc-chip-container' onClick={openPopper} onFocus={openPopper}>
                        {selectedRanges?.map((selectedRange: RangeEntryWrapper, index: number): ReactElement => {
                            const isSelected: boolean = currentlySelectedIndex === index

                            return (
                                <Chip
                                    className={`rc-chip ${isSelected ? 'selected' : EMPTY_STRING} ${
                                        !selectedRange.included ? 'excluded' : EMPTY_STRING
                                    } ${selectedRange.isRange ? 'range' : EMPTY_STRING}`}
                                    key={rangeWrapperToString(selectedRange) + index}
                                    label={rangeWrapperToString(selectedRange)}
                                    clickable
                                    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>): void => {
                                        if (event.key === 'Tab') {
                                            event.preventDefault()

                                            if (isMounted.current) {
                                                const nextIndex: number = event.shiftKey ? index - 1 : index + 1
                                                const nextChip: Nullable<HTMLElement> =
                                                    rcInputAreaRef?.current?.querySelectorAll('.rc-chip')?.[
                                                        nextIndex
                                                    ] as HTMLElement

                                                if (isVarNotEmpty(selectedRanges?.[nextIndex])) {
                                                    setCurrentlySelectedIndex(nextIndex)
                                                    setSearch(rangeWrapperToString(selectedRanges?.[nextIndex]))
                                                    openPopper()
                                                    nextChip?.focus()
                                                } else {
                                                    setCurrentlySelectedIndex(undefined)
                                                    setSearch(EMPTY_STRING)
                                                    setIsPopperOpen(false)
                                                }
                                            }
                                        }
                                    }}
                                    onClick={(): void => {
                                        if (isMounted.current) {
                                            setCurrentlySelectedIndex(isSelected ? undefined : index)
                                            setSearch(isSelected ? EMPTY_STRING : rangeWrapperToString(selectedRange))
                                            openPopper()
                                        }
                                    }}
                                    onDelete={(): void => {
                                        if (isMounted.current) {
                                            setSelectedRanges(
                                                (
                                                    currentlySelectedRanges: Nullable<RangeEntryWrapper[]>
                                                ): Nullable<RangeEntryWrapper[]> => {
                                                    if (currentlySelectedRanges) {
                                                        return currentlySelectedRanges.filter(
                                                            (_: RangeEntryWrapper, rangeIndex: number): boolean =>
                                                                rangeIndex !== index
                                                        )
                                                    }

                                                    return currentlySelectedRanges
                                                }
                                            )
                                        }
                                    }}
                                />
                            )
                        }) ?? <></>}
                        <Chip
                            className='rc-add-chip'
                            label={
                                <>
                                    {isAdding ? (
                                        <span
                                            contentEditable
                                            suppressContentEditableWarning
                                            className='rc-span-input'
                                            ref={spanInputRef}
                                            onInput={(event: FormEvent): void => {
                                                if (isMounted.current) {
                                                    setAddingValue(event?.currentTarget?.textContent ?? EMPTY_STRING)
                                                }
                                            }}
                                            onKeyDown={(event: React.KeyboardEvent<HTMLSpanElement>): void => {
                                                if (event.key === 'Escape') {
                                                    event.preventDefault()
                                                    setIsAdding(false)
                                                    setAddingValue(EMPTY_STRING)

                                                    return undefined
                                                }

                                                if (event.key === 'Enter') {
                                                    event.preventDefault()

                                                    return spanInputRef?.current?.blur()
                                                }

                                                if (event.key === 'Tab') {
                                                    event.preventDefault()
                                                    addNewOptions(event)
                                                    setIsAdding(true)
                                                    setTimeout((): void => spanInputRef?.current?.focus(), 100)

                                                    return undefined
                                                }

                                                return undefined
                                            }}
                                            onBlur={addNewOptions}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                    <div
                                        onClick={(): void => {
                                            setIsAdding(true)
                                            setTimeout((): void => spanInputRef?.current?.focus(), 100)
                                        }}
                                    >
                                        <Icon iconName={IconName.ADD_CIRCLE} className='rc-add' />
                                    </div>
                                </>
                            }
                        />
                        {isTextEntryMode ? (
                            <></>
                        ) : (
                            <PopperContent
                                className='rc-popper'
                                anchorEl={rcInputAreaRef.current}
                                open={isPopperOpen && !isTextEntryMode}
                                setOpen={setIsPopperOpen}
                                isClickAwayHandled
                                clickAwayGuard={(event: React.MouseEvent<Document, MouseEvent>): boolean =>
                                    !(
                                        (event?.target as HTMLElement)?.closest('.rc-input-area') ||
                                        (event?.target as HTMLElement)?.closest('.rc-popper')
                                    )
                                }
                                clickAwayCallBack={(): void => {
                                    if (isMounted.current) {
                                        setCurrentlySelectedIndex(undefined)
                                        setSearch(EMPTY_STRING)
                                    }
                                }}
                            >
                                <div className='items-search'>
                                    <TextInput
                                        placeholder={language.get(RangeComboLanguage.RangeCombo_Search_Placeholder)}
                                        value={search ?? EMPTY_STRING}
                                        onChange={(searchValue: string): void => {
                                            if (isMounted.current) {
                                                setSearch(searchValue)
                                            }
                                        }}
                                        adornmentEnd={
                                            <>
                                                <div
                                                    className='rc-adornment'
                                                    onClick={(): void => setSearch(EMPTY_STRING)}
                                                >
                                                    <Icon iconName={IconName.X} />
                                                </div>
                                            </>
                                        }
                                    />
                                </div>
                                <div className='items-wrapper'>
                                    {searchedOptions.map(
                                        (option: SelectOption<string>, index: number): ReactElement => (
                                            <div
                                                key={option.value + index}
                                                className='item'
                                                onClick={(): void => {
                                                    if (
                                                        isVarNotEmpty(allItemIndex) &&
                                                        option.value === options?.[allItemIndex]?.value
                                                    ) {
                                                        setSelectedRanges([])
                                                    } else {
                                                        setSelectedRanges(
                                                            (
                                                                currentRanges: Nullable<RangeEntryWrapper[]>
                                                            ): Nullable<RangeEntryWrapper[]> => {
                                                                let newRanges: Nullable<RangeEntryWrapper[]>

                                                                if (option.itemState === SelectRangeItemState.NONE) {
                                                                    newRanges = addRangeEntryWrapper(
                                                                        option.value,
                                                                        true,
                                                                        currentRanges
                                                                    )
                                                                } else if (
                                                                    option.itemState === SelectRangeItemState.EXCLUDED
                                                                ) {
                                                                    newRanges = addRangeEntryWrapper(
                                                                        option.value,
                                                                        true,
                                                                        removeRangeEntryWrapper(
                                                                            option.value,
                                                                            currentRanges
                                                                        )
                                                                    )
                                                                } else {
                                                                    newRanges = removeRangeEntryWrapper(
                                                                        option.value,
                                                                        currentRanges
                                                                    )
                                                                }

                                                                return newRanges
                                                            }
                                                        )
                                                    }
                                                }}
                                                onContextMenu={(event: React.MouseEvent): void => {
                                                    event.preventDefault()

                                                    if (
                                                        isVarNotEmpty(allItemIndex) &&
                                                        option.value === options?.[allItemIndex]?.value
                                                    ) {
                                                        setSelectedRanges([])
                                                    } else {
                                                        setSelectedRanges(
                                                            (
                                                                currentRanges: Nullable<RangeEntryWrapper[]>
                                                            ): Nullable<RangeEntryWrapper[]> => {
                                                                let newRanges: Nullable<RangeEntryWrapper[]>

                                                                if (option.itemState === SelectRangeItemState.NONE) {
                                                                    newRanges = addRangeEntryWrapper(
                                                                        option.value,
                                                                        false,
                                                                        currentRanges
                                                                    )
                                                                } else if (
                                                                    option.itemState === SelectRangeItemState.INCLUDED
                                                                ) {
                                                                    newRanges = addRangeEntryWrapper(
                                                                        option.value,
                                                                        false,
                                                                        removeRangeEntryWrapper(
                                                                            option.value,
                                                                            currentRanges
                                                                        )
                                                                    )
                                                                } else {
                                                                    newRanges = removeRangeEntryWrapper(
                                                                        option.value,
                                                                        currentRanges
                                                                    )
                                                                }

                                                                return newRanges
                                                            }
                                                        )
                                                    }
                                                }}
                                            >
                                                {toCheckBox(option?.itemState ?? SelectRangeItemState.NONE)}
                                                <div>{option.renderOptionElement ?? option.label}</div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </PopperContent>
                        )}
                    </div>
                    <div className='rc-end-adornments'>
                        <IconButton
                            iconName={isPopperOpen ? IconName.ARROW_DROPUP : IconName.ARROW_DROPDOWN}
                            type={IconButtonType.BUTTON}
                            onClick={(): void => setIsPopperOpen((popperOpen: boolean): boolean => !popperOpen)}
                        />
                        <IconButton iconName={IconName.PENCIL} type={IconButtonType.BUTTON} onClick={switchMode} />
                    </div>
                </div>
            )}
        </div>
    )
}
